import { fieldConfig } from "@autoform/zod";
import { z } from "zod";

import { Media, MediaLenient } from "@/components/forms/schemas/media";
import { artistNestedSchema } from "@/pages/artists/schemas/artist.nested.form";
import { reachField } from "@/pages/events/schemas/event.reach";
import { eventTicketingSchema } from "@/pages/events/schemas/event.ticketing.form";
import { checkVenueTba } from "@/pages/events/schemas/event.venue-tba";
import { genreNestedSchema } from "@/pages/genres/schemas/genre.nested.form";
import { geodataNestedFormSchema } from "@/pages/geodata/schemas/geodata.nested.form";
import { venueNestedSchema } from "@/pages/venues/schemas/venue.nested.form";

const eventVenueFormSchema = venueNestedSchema.superRefine(
  fieldConfig({
    fieldType: "venue",
  }),
);

export const basicEventSchema = z.object({
  name: z.string(),
  type: z.enum([
    "Event",
    "Party",
    "Live",
    "Concert",
    "DJ set",
    "Festival",
    "Panigiri",
    "Other",
  ]),
  // Form-only: folded into `metadata.notificationRules.locality` on save
  // (see event.reach.ts).
  reach: reachField,
  about: z
    .string()
    .nullish()
    .superRefine(
      fieldConfig({
        fieldType: "textarea",
      }),
    ),
  venue: eventVenueFormSchema.nullish(),
  mapsUrl: z
    .string()
    .url()
    .nullish()
    .superRefine(
      fieldConfig({
        fieldType: "mapsUrl",
      }),
    ),
  venueTba: z
    .boolean()
    .default(false)
    .superRefine(fieldConfig({ fieldType: "venueTba" })),
  geodata: geodataNestedFormSchema,
  lineup: z
    .array(
      artistNestedSchema.superRefine((data, ctx) => {
        if (data.id && !data.photo) {
          ctx.addIssue({
            code: "custom",
            message:
              "If an artist comes from our database, they must have a photo",
            path: ["photo"],
          });
        }
      }),
    )
    .nullish()
    .default([])
    .superRefine(
      fieldConfig({
        fieldType: "lineup",
      }),
    ),
  startDate: z
    .string()
    .datetime({ offset: true })
    .superRefine(
      fieldConfig({
        fieldType: "datetime",
      }),
    ),
  endDate: z
    .string()
    .datetime({ offset: true })
    .nullish()
    .superRefine(fieldConfig({ fieldType: "datetime" })),
  genres: z
    .array(genreNestedSchema)
    .default([])
    .superRefine(fieldConfig({ fieldType: "genres" })),
  geolocation: z.object({
    latitude: z.coerce.number(),
    longitude: z.coerce.number(),
  }),
  timezone: z.string(),
  ticketUrl: z.string().url().nullish(),
  metadata: z
    .object({
      id: z.string(),
      url: z.string().url().optional(),
      site: z.string().optional(),
      jobId: z.string().optional(),
      timestamp: z.string().optional(),
      source: z.string().optional(),
      // Carried through the create payload (not curator-editable). The Tina
      // low-score "Review & edit" handoff stamps this to 1 — a curator review is
      // a full-confidence signal. Hidden so it never renders an input.
      confidence: z
        .number()
        .nullish()
        .superRefine(fieldConfig({ fieldType: "hidden" })),
    })
    .nullish(),
});

/**
 * What the create form validates and hands its ZodProvider. Refined, so it is a
 * ZodEffects: `basicEventSchema` stays the plain object for `.shape` and `.merge`.
 */
export const basicEventSubmitSchema =
  basicEventSchema.superRefine(checkVenueTba);

export const eventTicketingStepSchema = z.object({
  ticketing: eventTicketingSchema
    .pick({ url: true, provider: true, providerMetadata: true })
    .nullish(),
});

export const mediaEventSchema = z.object({
  id: z.string().superRefine(fieldConfig({ inputProps: { disabled: true } })),
  hide: z.boolean().default(false),
  media: Media.superRefine(
    fieldConfig({
      fieldType: "media",
      customData: {
        collection: "Events",
      },
    }),
  ),
  tags: z
    .array(z.string())
    .superRefine(fieldConfig({ fieldType: "eventTags" })),
});

export const draftEventSchema = basicEventSchema
  .merge(eventTicketingStepSchema)
  .merge(mediaEventSchema.extend({ media: MediaLenient.nullish() }));

export type EventBasicInfoType = z.infer<typeof basicEventSchema>;
export type EventTicketingType = z.infer<typeof eventTicketingStepSchema>;
export type EventMediaType = z.infer<typeof mediaEventSchema>;
export type EventDraftType = z.infer<typeof draftEventSchema>;
